// src/context/ProcurementContext.jsx
import React, { createContext, useContext, useState, useEffect } from "react";
import { useInventory } from "./InventoryContext";

const ProcurementContext = createContext();
export const useProcurement = () => useContext(ProcurementContext);

export const ProcurementProvider = ({ children }) => {
  const { addStock, removeStock, addPurchaseRecord, getInventory } = useInventory();

  const [procurements, setProcurements] = useState(() => {
    try {
      const saved = localStorage.getItem("procurements");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem("procurements", JSON.stringify(procurements));
  }, [procurements]);

  const saveProcurement = (data) => {
    const formatted = {
      ...data,
      quantity: Number(data.quantity || 0),
      budgetAllocated: Number(data.budgetAllocated || 0),
    };

    if (formatted.materialId) {
      const inv = getInventory(formatted.materialId);
      formatted.materialName = inv ? inv.itemName || inv.name : formatted.materialName;
      formatted.inventoryId = formatted.materialId;

      // ✅ Reserve stock when procurement is added (optional)
      if (!formatted.id && formatted.status !== "Purchased") {
      }
    }

    if (formatted.id) {
      setProcurements(prev =>
        prev.map(p => String(p.id) === String(formatted.id) ? formatted : p)
      );
    } else {
      setProcurements(prev => [...prev, { ...formatted, id: Date.now().toString() }]);
    }
  };

  const deleteProcurement = (id) => {
    // ✅ If stock was reserved, add it back
    const p = getProcurement(id);
    if (p && p.materialId && p.status !== "Purchased") {
      addStock(p.materialId, p.quantity, "Procurement Cancelled");
    }

    setProcurements(prev => prev.filter(p => String(p.id) !== String(id)));
  };

  const getProcurement = (id) => procurements.find(p => String(p.id) === String(id));

  const handleDragEnd = (result) => {
    const { destination, draggableId, source } = result || {};
    if (!destination || source.droppableId === destination.droppableId) return;

    setProcurements(prev =>
      prev.map(p =>
        String(p.id) === String(draggableId)
          ? { ...p, status: destination.droppableId }
          : p
      )
    );
  };

  const approveProcurement = (id) => {
    const p = getProcurement(id);
    if (!p || !p.materialId) return;

    // ✅ Add stock to inventory when procurement approved
    addStock(p.materialId, p.quantity, "Procurement Received");

    addPurchaseRecord(p.materialId, {
      vendor: p.vendorName,
      qty: p.quantity,
      poNumber: p.poNumber,
      projectName: p.projectName,
      cost: p.budgetAllocated,
    });

    setProcurements(prev =>
      prev.map(i =>
        String(i.id) === String(id)
          ? { ...i, status: "Purchased", purchasedAt: new Date().toISOString() }
          : i
      )
    );
  };

  return (
    <ProcurementContext.Provider
      value={{
        procurements,
        saveProcurement,
        deleteProcurement,
        getProcurement,
        handleDragEnd,
        approveProcurement,
      }}
    >
      {children}
    </ProcurementContext.Provider>
  );
};
